import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Pagination,
  Stack,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import CommonLabel from "../label";

const CommonPagination = ({
  page = 1,
  totalPages = 1,
  pageSize = 20,
  totalCount = 0,
  onPageChange = () => {},
  showResultCount = true,
}) => {
  const handlePreviousPage = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  const handlePageClick = (event, pageNumber) => {
    onPageChange(pageNumber);
  };

  const startResult = (page - 1) * pageSize + 1;
  const endResult = Math.min(page * pageSize, totalCount);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: { xs: "center", sm: "space-between" },
        alignItems: "center",
        gap: { xs: 1, sm: 0 },
        py: 2,
        px: 1,
        bgcolor: "#fff",
        borderRadius: "8px",
      }}
    >
      {/* Result Count Display */}
      {showResultCount && (
        <CommonLabel
          variant={"body2"}
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            fontWeight: 500,
            color: "#666",
            textAlign: { xs: "center", sm: "left" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Showing {startResult} – {endResult} of {totalCount} results
        </CommonLabel>
      )}

      {/* Pagination Controls */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "nowrap",
          ml: { xs: 0, sm: "auto" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {/* Previous Button */}
        <IconButton
          onClick={handlePreviousPage}
          disabled={page === 1}
          size="small"
          sx={{
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: { xs: "4px", sm: "8px" },
            "&:hover": {
              bgcolor: "#f0f0f0",
              borderColor: "#999",
            },
            "&:disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
        </IconButton>

        {/* Page Numbers */}
        <Box>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageClick}
            variant="outlined"
            shape="rounded"
            size="small"
            siblingCount={0}
            boundaryCount={1}
            hidePrevButton
            hideNextButton
            sx={{
              "& .MuiPagination-ul": {
                flexWrap: "nowrap",
              },
              "& .MuiPaginationItem-root": {
                border: "1px solid #ddd",
                minWidth: { xs: "28px", sm: "32px" },
                height: { xs: "28px", sm: "32px" },
                fontSize: { xs: "12px", sm: "13px" },
                margin: "0 1px",
                "&:hover": {
                  bgcolor: "#f5f5f5",
                },
              },
              "& .MuiPaginationItem-page.Mui-selected": {
                bgcolor: "#111",
                color: "#fff",
                border: "1px solid #111",
                "&:hover": {
                  bgcolor: "#333",
                },
              },
            }}
          />
        </Box>

        {/* Next Button */}
        <IconButton
          onClick={handleNextPage}
          disabled={page === totalPages}
          size="small"
          sx={{
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: { xs: "4px", sm: "8px" },
            "&:hover": {
              bgcolor: "#f0f0f0",
              borderColor: "#999",
            },
            "&:disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          }}
        >
          <ChevronRight sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default CommonPagination;
